export type TextArrayLiteral = string & {
  readonly __brand: 'TextArrayLiteral';
};

export const encodeTextArray = (
  values: readonly string[] | null | undefined,
): TextArrayLiteral | null =>
  values != null && values.length > 0
    ? (`{${values
        .map(value => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)
        .join(',')}}` as TextArrayLiteral)
    : null;

export const EXTERNAL_TRANSACTION_SOURCES = ['dvf_fr'] as const;
export type ExternalTransactionSource =
  (typeof EXTERNAL_TRANSACTION_SOURCES)[number];

export const TRANSACTION_SOURCES = [
  'crm',
  ...EXTERNAL_TRANSACTION_SOURCES,
] as const;
export type TransactionSource = (typeof TRANSACTION_SOURCES)[number];

export const EXTERNAL_TRANSACTIONS_MAX_RESULTS_PER_SOURCE = 2000;

export type ExternalTransactionBody = {
  id: string;
  source: ExternalTransactionSource;
  external_id: string;
  country_code: string;
  currency: string;
  signature_date: string | null;
  purchase_price: number | null;
  property_main_type: string | null;
  property_type_name: string | null;
  living_surface: number | null;
  land_surface: number | null;
  built_surface: number | null;
  number_of_rooms: number | null;
  number_of_bedrooms: number | null;
  locality: string | null;
  postcode: string | null;
  street_number: string | null;
  street_suffix: string | null;
  street_type: string | null;
  street_name: string | null;
  lat: number | null;
  lng: number | null;
};

export type ExternalTransactionDistanceRow = ExternalTransactionBody & {
  distance_in_meters: number;
};

export type CmaTransactionRow<TBase, TCrmTx, TExternalTx> =
  | (TBase & {
      source: 'crm';
      external_id: null;
      transaction: TCrmTx | null;
    })
  | (TBase & {
      source: ExternalTransactionSource;
      external_id: string | null;
      transaction: TExternalTx | null;
    });

export type ExternalTransactionRef = {
  source: ExternalTransactionSource;
  externalId: string;
};

export const isExternalTransactionSource = (
  source: TransactionSource,
): source is ExternalTransactionSource => source !== 'crm';

export const externalTransactionKey = (
  source: ExternalTransactionSource,
  externalId: string,
) => `${source}:${externalId}`;

export const getLatestNonFutureSignatureDate = <
  TRow extends { signature_date?: string | null },
>(
  rows: readonly TRow[],
  maxDate = new Date().toISOString().slice(0, 10),
): string | null =>
  rows.reduce<string | null>((latest, row) => {
    const signatureDate = row.signature_date?.slice(0, 10) ?? null;
    if (
      signatureDate == null ||
      signatureDate > maxDate ||
      (latest != null && signatureDate <= latest)
    ) {
      return latest;
    }
    return signatureDate;
  }, null);

export const extractExternalTransactionRefs = <
  TRow extends { source: TransactionSource; external_id: string | null },
>(
  rows: readonly TRow[],
): ExternalTransactionRef[] =>
  rows.flatMap(row => {
    if (!isExternalTransactionSource(row.source) || row.external_id == null) {
      return [];
    }
    return [{ source: row.source, externalId: row.external_id }];
  });

type PolymorphicCmaRow<TBody, TDetails> = {
  source: TransactionSource;
  external_id: string | null;
  transaction: TBody | TDetails | null;
};

export const hydrateExternalTransactions = <
  TBody,
  TDetails extends {
    source: ExternalTransactionSource;
    external_id: string;
  },
  TRow extends PolymorphicCmaRow<TBody, TDetails>,
>(
  rows: readonly TRow[],
  details: readonly TDetails[] | Map<string, TDetails> | null | undefined,
) => {
  const detailsByKey: ReadonlyMap<string, TDetails> =
    details instanceof Map
      ? details
      : new Map(
          (details ?? []).map(detail => [
            externalTransactionKey(detail.source, detail.external_id),
            detail,
          ]),
        );

  return rows.map(row => {
    if (!isExternalTransactionSource(row.source) || row.external_id == null) {
      return row;
    }
    return {
      ...row,
      transaction:
        detailsByKey.get(externalTransactionKey(row.source, row.external_id)) ??
        null,
    };
  });
};

const TRANSACTION_OUTLIER_CONFIG = {
  MIN_RATIO: 0.5,
  MAX_RATIO: 1.8,
} as const;

const isTransactionOutlier = (
  transactionPrice: number,
  propertyValuation: number,
) => {
  if (propertyValuation <= 0) return false;
  const ratio = transactionPrice / propertyValuation;

  return (
    ratio < TRANSACTION_OUTLIER_CONFIG.MIN_RATIO ||
    ratio > TRANSACTION_OUTLIER_CONFIG.MAX_RATIO
  );
};

type CrmRowLike = {
  distance_in_meters?: number | null;
  transaction?: { purchase_price?: number | null } | null;
};

type ExternalRowLike = {
  distance_in_meters?: number | null;
  purchase_price?: number | null;
};

export const rankCmaTransactions = <
  TCrm extends CrmRowLike,
  TExternal extends ExternalRowLike,
>(
  crmRows: readonly TCrm[],
  externalRows: readonly TExternal[],
  propertyValuation: number | null,
  limit = 10,
): Array<
  | { kind: 'crm'; distance: number; row: TCrm }
  | { kind: 'external'; distance: number; row: TExternal }
> => {
  const canDetectOutliers = propertyValuation != null && propertyValuation > 0;

  const ranked = [
    ...crmRows.map(row => ({
      kind: 'crm' as const,
      distance: Number(row.distance_in_meters ?? Number.POSITIVE_INFINITY),
      row,
    })),
    ...externalRows.map(row => ({
      kind: 'external' as const,
      distance: Number(row.distance_in_meters ?? Number.POSITIVE_INFINITY),
      row,
    })),
  ];

  const filtered = canDetectOutliers
    ? ranked.filter(entry => {
        const price =
          entry.kind === 'crm'
            ? entry.row.transaction?.purchase_price ?? null
            : entry.row.purchase_price ?? null;
        if (price == null || price <= 0) {
          return true;
        }
        return !isTransactionOutlier(price, propertyValuation);
      })
    : ranked;

  return filtered.sort((a, b) => a.distance - b.distance).slice(0, limit);
};

export const getCrmTransactionCoverageDistance = <TCrm extends CrmRowLike>(
  crmRows: readonly TCrm[],
  propertyValuation: number | null,
  limit = 10,
): number | null => {
  const crmPool = rankCmaTransactions<TCrm, never>(
    crmRows,
    [],
    propertyValuation,
    limit,
  );

  return crmPool.length < limit
    ? null
    : crmPool[crmPool.length - 1]?.distance ?? null;
};

export const filterExternalTransactionsWithinCrmRange = <
  TCrm extends CrmRowLike,
  TExternal extends ExternalRowLike,
>(
  crmRows: readonly TCrm[],
  externalRows: readonly TExternal[],
  propertyValuation: number | null,
  limit = 10,
): TExternal[] => {
  const farthestCrmDistance = getCrmTransactionCoverageDistance(
    crmRows,
    propertyValuation,
    limit,
  );
  if (farthestCrmDistance == null) {
    return [...externalRows];
  }
  return externalRows.filter(
    row =>
      Number(row.distance_in_meters ?? Number.POSITIVE_INFINITY) <=
      farthestCrmDistance,
  );
};

const capitalizeAddressPart = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const capitalizeAddressWords = (value: string) =>
  value
    .toLowerCase()
    .replace(/(^|[\s'-])[a-zà-öø-ÿœ]/g, part => part.toUpperCase());

const restoreFrenchElisions = (value: string) =>
  value.replace(/\b([dl])\s+(?=[aeiouyàâäéèêëîïôöùûüœh])/gi, "$1'");

export const formatExternalStreetAddress = (data: {
  country_code?: string | null;
  street_number?: string | null;
  street_suffix?: string | null;
  street_type?: string | null;
  street_name?: string | null;
}) => {
  const number = data.street_number?.trim() || null;
  const suffix = data.street_suffix?.trim()
    ? capitalizeAddressPart(data.street_suffix.trim())
    : null;
  const type = data.street_type?.trim()
    ? capitalizeAddressPart(data.street_type.trim())
    : null;
  const name = data.street_name?.trim()
    ? capitalizeAddressWords(
        data.country_code === 'FR'
          ? restoreFrenchElisions(data.street_name.trim())
          : data.street_name.trim(),
      )
    : null;
  return (
    [number, suffix, type, name]
      .filter((part): part is string => part != null)
      .join(' ') || null
  );
};
